import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import './InvoicePrintPages.css';

interface IProductRow {
  Name: string;
  CostInCents: number;
  Quantity: number;
  SubTotalInCents: number;
  SavingsInCents: number;
  TotalInCents: number;
  IsRegistrationFee: boolean;
}

interface ICouponRow {
  Code: string;
  IsAffiliateSubsidy: boolean;
  SavingsInCents: number;
  UsedCount: number;
}

interface ILineItem {
  Name: string;
  Detail: string | null;
  Quantity: number;
  CostInCents: number;
  LineTotalInCents: number;
  Refunded: boolean;
}

interface IInvoiceCoupon {
  Code: string;
  SavingsInCents: number;
}

interface IInvoiceRow {
  OrderId: number;
  PublicId: string;
  InvoiceUrl: string;
  LineItems: ILineItem[];
  Coupons: IInvoiceCoupon[];
}

interface IInvoiceSummaryData {
  IsAdmin: boolean;
  IsHistoryContest: boolean;
  AffiliateLocationCode: string | null;
  AffiliateName: string | null;
  ProductRows: IProductRow[];
  CouponRows: ICouponRow[];
  TotalPaidViaInvoicesInCents: number;
  TotalCouponSubsidyInCents: number;
  Invoices: IInvoiceRow[];
}

const centsToMoney = (cents: number) => {
  const value = (cents || 0) / 100;
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

type Mode = 'admin' | 'affiliate';

interface InvoiceSummaryPageProps {
  mode: Mode;
}

const InvoiceSummaryPage = (props: InvoiceSummaryPageProps) => {
  const { clientId } = useParams<{ clientId?: string }>();
  const [searchParams] = useSearchParams();
  const onlyCard = searchParams.get('onlyCardTransactions') === 'true';
  const [data, setData] = useState<IInvoiceSummaryData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url =
      props.mode === 'admin'
        ? `../Purchase/GetAdminInvoiceSummaryData?onlyCardTransactions=${onlyCard}`
        : `../Purchase/GetAffiliateInvoiceSummaryData/${clientId}`;

    store.server
      .getApi2<IInvoiceSummaryData>(url)
      .then((res: ServerResponse<IInvoiceSummaryData>) => {
        if (res.Success) {
          setData(res.Value);
        } else {
          toast.error(res.Message || 'Unable to load invoice summary.', { autoClose: false });
        }
      })
      .finally(() => setLoading(false));
  }, [props.mode, clientId, onlyCard]);

  if (loading) {
    return (
      <div className="invoice-print-page" data-testid="invoice-summary-page">
        <p>Loading invoice summary…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="invoice-print-page" data-testid="invoice-summary-page">
        <div className="alert alert-danger">Unable to load invoice summary.</div>
      </div>
    );
  }

  const showNhdAddress = data.IsAdmin || data.IsHistoryContest;
  const savingsHeader = data.IsAdmin ? 'Coupon/Subsidy Savings' : 'Coupon';

  return (
    <div className="invoice-print-page" data-testid="invoice-summary-page">
      <div className="row">
        <div className="col-sm-9">
          <h3>{data.IsAdmin ? 'Admin Order Summary' : 'Affiliate Order Summary'}</h3>
          {!data.IsAdmin && (
            <h4>
              {data.AffiliateLocationCode}
              {data.AffiliateName ? ` - ${data.AffiliateName}` : ''}
            </h4>
          )}
        </div>
        <div className="col-sm-3 text-right">
          <br />
          <button type="button" className="btn btn-default hidden-print" onClick={() => window.print()}>
            <i className="fa fa-print" />&nbsp;Print
          </button>
        </div>
      </div>

      {showNhdAddress && (
        <div className="row">
          <div className="col-sm-3">
            <h5>Send Payments To:</h5>
            <br />
            <span>National History Day</span>
            <br />
            <span>4511 Knox Road, Suite 205</span>
            <br />
            <span>College Park, MD 20740</span>
          </div>
          <div className="col-sm-3" />
          <div className="col-sm-3" />
          <div className="col-sm-3 text-right">
            <br />
            <span>https://www.nhd.org</span>
            <br />
            <span>(301) 314-9739</span>
            <br />
            <span>info@nhd.org</span>
          </div>
        </div>
      )}

      <br />
      <br />

      <div className="panel panel-default">
        <div className="panel-heading">
          <strong>Summary</strong>
        </div>
        <div className="panel-body">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Products</th>
                <th style={{ width: '12%' }} className="text-right">
                  Cost
                </th>
                <th style={{ width: '8%' }} className="text-right">
                  Quantity
                </th>
                <th style={{ width: '12%' }} className="text-right">
                  Sub-Total
                </th>
                <th style={{ width: '12%' }} className="text-right">
                  {savingsHeader}
                </th>
                <th style={{ width: '12%' }} className="text-right">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {(data.ProductRows || []).map((p, i) => (
                <tr key={`p-${i}`}>
                  <td>
                    <i>{p.Name}</i>
                    {p.IsRegistrationFee && (
                      <span className="text-muted" style={{ fontSize: 'smaller' }}>
                        {' '}
                        * does not account for late fees
                      </span>
                    )}
                  </td>
                  <td className="text-right">${centsToMoney(p.CostInCents)}</td>
                  <td className="text-right">{(p.Quantity || 0).toLocaleString('en-US')}</td>
                  <td className="text-right">${centsToMoney(p.SubTotalInCents)}</td>
                  <td className="text-right">${centsToMoney(p.SavingsInCents)}</td>
                  <td className="text-right">${centsToMoney(p.TotalInCents)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={6}>&nbsp;</td>
              </tr>
              <tr>
                <th>Coupons / Subsidies</th>
                <th />
                <th />
                <th />
                <th />
                <th className="text-right">Total</th>
              </tr>
              {(data.CouponRows || []).map((c, i) => (
                <tr key={`c-${i}`}>
                  <td>
                    <i>{c.Code}</i>
                    {c.IsAffiliateSubsidy && <span className="label label-default">Affiliate Subsidy</span>}
                    {!data.IsAdmin && <> Used: {c.UsedCount};</>}
                  </td>
                  <td />
                  <td />
                  <td />
                  <td />
                  <td className="text-right">$ {centsToMoney(c.SavingsInCents)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={6}>&nbsp;</td>
              </tr>
              <tr>
                <td colSpan={5} className="text-right">
                  <strong>Total Paid Via Invoices:</strong>
                </td>
                <td className="text-right">
                  <strong>$ {centsToMoney(data.TotalPaidViaInvoicesInCents)}</strong>
                </td>
              </tr>
              <tr>
                <td colSpan={5} className="text-right">
                  <strong>Total Coupon / Subsidy:</strong>
                  <br />
                  <i>This is the amount due.</i>
                </td>
                <td className="text-right">
                  <strong>$ {centsToMoney(data.TotalCouponSubsidyInCents)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel panel-default">
        <div className="panel-heading">
          <strong>Invoices</strong>
        </div>
        <div className="panel-body">
          <table className="table">
            <tbody>
              {(data.Invoices || []).map((invoice) => (
                <tr key={invoice.OrderId}>
                  <td style={{ width: '10%', verticalAlign: 'top' }}>
                    <strong>
                      <a
                        className="hidden-print"
                        href={StoreX.BuildUrl(invoice.InvoiceUrl)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <i className="fa fa-file-text-o" />&nbsp;{invoice.OrderId}
                      </a>
                      <span className="visible-print">{invoice.OrderId}</span>
                    </strong>
                  </td>
                  <td>
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th className="text-right">#</th>
                          <th className="text-right">Cost</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(invoice.LineItems || []).map((line, li) => (
                          <tr
                            key={`${invoice.OrderId}-line-${li}`}
                            style={line.Refunded ? { textDecoration: 'line-through' } : undefined}
                          >
                            <td>
                              {line.Name}
                              {line.Detail && <div className="text-muted">{line.Detail}</div>}
                            </td>
                            <td className="text-right" style={{ width: '5%' }}>
                              {line.Quantity}
                            </td>
                            <td className="text-right" style={{ width: '10%' }}>
                              $&nbsp; {centsToMoney(line.CostInCents)}
                            </td>
                            <td className="text-right" style={{ width: '10%' }}>
                              $&nbsp; {centsToMoney(line.LineTotalInCents)}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={4}>&nbsp;</td>
                        </tr>
                        {(invoice.Coupons || []).map((c, ci) => (
                          <tr key={`${invoice.OrderId}-coupon-${ci}`}>
                            <td>
                              Coupon: <strong>{c.Code}</strong>
                            </td>
                            <td />
                            <td className="text-right">Savings</td>
                            <td className="text-right">$ {centsToMoney(c.SavingsInCents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSummaryPage;
